import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { PermissionLevel, SessionService, TeamMember, UiArguments } from "@recore-solutions/nstar-lib";
import { of } from "rxjs";
import { map, switchMap, take } from "rxjs/operators";
import { CodeTableService } from "src/app/listing/code-table.service";
import { ListingService } from "./listing.service";
import { LoonFnFactoriesService } from "./loon-fn-factories.service";

@Injectable({ providedIn: "root" })
export class TeamMemberFnFactoriesService extends LoonFnFactoriesService {
	constructor(
		protected listingService: ListingService,
		protected sessionService: SessionService,
		protected codeTableService?: CodeTableService
	) {
		super(listingService, codeTableService);
	}

	queryTransformFn = (uiArguments: UiArguments) => {
		const children = this.getChildrenFromUiArguments(uiArguments);
		return (term: string, additionalParams?: any[]) => {
			const fromObject: any = {};
			additionalParams = additionalParams?.filter((p) => !!p);
			if (typeof term === "number") {
				fromObject.TeamMember_SK = (term as number).toString();
				return new HttpParams({ fromObject });
			} else if (!!additionalParams?.length) {
				children.forEach((u, i) => {
					if (additionalParams?.[i] !== undefined && additionalParams?.[i] !== null) {
						fromObject[u.longName] =
							additionalParams[i]?.toJSON?.() ||
							additionalParams[i]?.toString?.() ||
							additionalParams[i];
					}
				});
				fromObject["Team.Expiration_UTCDTTM"] = "FUTURE";
				fromObject["TeamMember.Expiration_UTCDTTM"] = "FUTURE";
				fromObject["OfficeMember.Expiration_UTCDTTM"] = "FUTURE";
				return new HttpParams({ fromObject });
			} else if (!!term) {
				fromObject.memberSearch = term;
				fromObject["Team.Expiration_UTCDTTM"] = "FUTURE";
				fromObject["TeamMember.Expiration_UTCDTTM"] = "FUTURE";
				fromObject["OfficeMember.Expiration_UTCDTTM"] = "FUTURE";
				return new HttpParams({ fromObject });
			}
			return null;
		};
	};

	searchFn = (uiArguments: UiArguments) => {
		const { listingOrganizationSK } = uiArguments.listingSummary;
		const permissionLevel$ = this.sessionService.permissionLevel$.pipe(
			map((permissionLevels: PermissionLevel[]) =>
				this.mapPermissionLevelsToPermissionLevel(permissionLevels, listingOrganizationSK)
			)
		);
		return (queryObj: HttpParams) => {
			const { listingStatusCD } = this.getListingSummaryExtract(uiArguments.formGroup);
			if (!queryObj || queryObj.keys().length === 0) {
				return of([]);
			}

			// sale/co-sales agent when status is pending, sold or rented
			const sellingFields = [
				"Selling Agent",
				"Co-Selling Agent",
				"Renting Agent",
				"Co-Renting Agent",
			];
			if (
				["PEND", "CLOSD", "RENTD"].includes(listingStatusCD) &&
				queryObj?.has("memberSearch") &&
				sellingFields.includes(this.getParentFromUiArguments(uiArguments).longName)
			) {
				const httpParams = new HttpParams({
					fromObject: {
						accessOverride: "Y",
						"OfficeMember.Expiration_UTCDTTM": "FUTURE",
						fullTextSearch: queryObj.get("memberSearch"),
					},
				});

				return this.getSearchMembers(httpParams).pipe(
					switchMap((members) => {
						const httpParams = new HttpParams({
							fromObject: {
								"Member.Member_SK": members.map((tm) => tm.memberSK).join(","),
							},
						});
						return this.getTeamMembers(httpParams);
					})
				);
			}

			// this is for adding a new/different co-listing agent
			if (["Co-Listing Agent"].includes(this.getParentFromUiArguments(uiArguments).longName)) {
				if (queryObj?.has("memberSearch")) {
					const httpParams = new HttpParams({
						fromObject: {
							accessOverride: "Y",
							"OfficeMember.Expiration_UTCDTTM": "FUTURE",
							fullTextSearch: queryObj.get("memberSearch"),
						},
					});
					return this.getSearchMembers(httpParams);
				}
			}

			return permissionLevel$.pipe(
				take(1),
				switchMap(this.switchMapBasedOnPermissionLevel(queryObj))
			);
		};
	};

	switchMapBasedOnPermissionLevel =
		(queryObj: HttpParams) => (permissionLevels: PermissionLevel[]) => {
			const permissionLevel = permissionLevels.find((pl) => pl.primaryOfficeIND === "Y");
			switch (permissionLevel?.accessName) {
				case "Office Broker":
					if (queryObj?.has("memberSearch")) {
						queryObj = queryObj.set(
							"Office.Office_SK",
							permissionLevels.map((pl) => pl.officeSK).join(",")
						);
					}
					break;
				case "Head Broker":
					if (queryObj?.has("memberSearch")) {
						queryObj = queryObj.set("Office.Firm_SK", permissionLevel.firmSK);
					}
					break;
				case "Association Staff":
				case "Organization Staff":
				case "Super User":
					break;
				case "Agent Edit":
					if (queryObj?.has("memberSearch")) {
						queryObj = queryObj.set("CoTeamMember.Member_SK", permissionLevel?.memberSK);
					}
					break;
				case "Agent Edit Limited":
					if (queryObj?.has("memberSearch")) {
						queryObj = queryObj.set("CoTeamMember.Member_SK", permissionLevel?.memberSK);
						queryObj = queryObj.set("SingleMemberTeam_IND", "Y");
					}
				default:
					if (queryObj?.has("memberSearch")) {
						return this.getTeamMembers(
							this.mapPermissionLevelToTeamHttpParams(permissionLevel, queryObj)
						).pipe(switchMap(this.getCoTeamMembers(queryObj)));
					} else {
						return this.getTeams(queryObj);
					}
			}
			if (queryObj?.has("memberSearch")) {
				return this.getTeamMembers(
					this.mapPermissionLevelToTeamHttpParams(permissionLevel, queryObj)
				).pipe(switchMap(this.getCoTeamMembers(queryObj)));
			} else {
				return this.getTeams(queryObj);
			}
		};

	resultTransformFn = () => (results: any[]) =>
		[...results].sort((a, b) => a.teamName.toLowerCase().localeCompare(b.teamName.toLowerCase()));

	mapPermissionLevelToTeamHttpParams = (
		permissionLevel: PermissionLevel,
		httpParams?: HttpParams
	) => {
		httpParams = httpParams || new HttpParams();

		httpParams = httpParams.set("Team.Expiration_UTCDTTM", "FUTURE");
		httpParams = httpParams.set("TeamMember.Expiration_UTCDTTM", "FUTURE");

		return httpParams;
	};

	getCoTeamMembers = (queryObj: HttpParams) => (teams: TeamMember[]) => {
		let httpParams = new HttpParams({
			fromObject: {
				"Team.Team_SK": teams.map((tm) => tm.teamSK).join(","),
			},
		});
		if (queryObj?.has("memberSearch")) {
			httpParams = httpParams.set("memberSearch", queryObj.get("memberSearch"));
		}
		return this.getTeamMembers(httpParams);
	};
}
